import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Truck, Menu, MicOff, PhoneOff } from 'lucide-react';
import { Driver } from '../data/mockData';
interface CallCardProps {
  driver: Driver;
  onEndCall: () => void;
}
function SignalBars({
  bars
}: {
  bars: number;
}) {
  if (bars === 0) {
    return <img src="https://cdn.magicpatterns.com/uploads/qtiWE7RgqX6uR9cCddEQgD/android_cell_4_bar_off_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24.svg" alt="No signal" className="w-4 h-4 ml-0.5 opacity-50" data-id="element-108" />;
  }
  const heights = [4, 7, 10, 13];
  return <svg width="14" height="14" viewBox="0 0 14 14" className="ml-0.5" data-id="element-109">
      {heights.map((h, i) => <rect key={i} x={i * 3.5} y={14 - h} width="2.5" height={h} rx="0.5" fill={i < bars ? '#cbd5e1' : '#334155'} data-id="element-110" />)}
    </svg>;
}
export function CallCard({
  driver,
  onEndCall
}: CallCardProps) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(s => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const formatTime = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  // Get initials from name
  const initials = driver.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  return <motion.div drag dragConstraints={{
    left: -1000,
    right: 0,
    top: -1000,
    bottom: 0
  }} dragElastic={0.1} dragMomentum={false} initial={{
    opacity: 0,
    y: 100,
    scale: 0.9
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: 100,
    scale: 0.9
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 300
  }} style={{
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.75)'
  }} className="fixed bottom-6 right-6 z-50 w-[320px] h-[380px] bg-[#0f1523] rounded-xl border border-slate-800 flex flex-col overflow-hidden cursor-grab active:cursor-grabbing" data-id="element-111">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/50 bg-[#0a0f1a]/50" data-id="element-112">
        <div className="flex items-center gap-2 bg-slate-800/50 px-2.5 py-1 rounded-md" data-id="element-113">
          <Truck className="w-3.5 h-3.5 text-slate-400" data-id="element-114" />
          <span className="text-xs font-bold text-slate-200 font-mono tracking-tight" data-id="element-115">
            {driver.truckId}
          </span>
          <SignalBars bars={driver.signalBars ?? 4} data-id="element-116" />
        </div>
        <button className="text-slate-400 hover:text-slate-200 transition-colors" data-id="element-117">
          <Menu className="w-5 h-5" data-id="element-118" />
        </button>
      </div>

      {/* Center Content */}
      <div className="flex-1 flex flex-col items-center justify-center pt-4 pb-8 pointer-events-none" data-id="element-119">
        {/* Avatar */}
        <div className="w-[120px] h-[120px] rounded-full bg-[#d1d5db] flex items-center justify-center shadow-inner mb-6" data-id="element-120">
          <span className="text-[40px] font-bold text-slate-900 tracking-tight" data-id="element-121">
            {initials}
          </span>
        </div>

        {/* Timer */}
        <div className="text-slate-200 font-mono text-lg tracking-wider" data-id="element-122">
          {formatTime(seconds)}
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="flex items-center justify-center gap-6 pb-8" data-id="element-123">
        <button className="w-14 h-14 rounded-full bg-[#d1d5db] flex items-center justify-center hover:bg-white transition-colors shadow-lg" data-id="element-124">
          <MicOff className="w-6 h-6 text-slate-700" data-id="element-125" />
        </button>
        <button onClick={onEndCall} className="w-14 h-14 rounded-full bg-[#ef4444] flex items-center justify-center hover:bg-red-400 transition-colors shadow-lg shadow-red-500/20" data-id="element-126">
          <PhoneOff className="w-6 h-6 text-white" data-id="element-127" />
        </button>
      </div>
    </motion.div>;
}